import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { ClipboardInput } from "Components/Utils/CopyToClipboard";
import { I18N_NS } from "Config/i18n";
import { Button, Col, Form, Input, InputNumber, Radio, Row, Switch } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ExternalApplicationsFormProps extends ModalComponentProps {
  isNew?: boolean;
  onSubmit: (values: API_ClientApp) => Promise<void>;
  initialData?: API_ClientApp;
  getClientIdAndSecret: () => Promise<API_ClientInformation>;
}

export const ExternalApplicationsForm = ({
  initialData = { appSecurityType: "OAUTH2", tokenLifeTimeInHours: 48 },
  onSubmit,
  hideModal,
  getClientIdAndSecret,
  isNew,
}: ExternalApplicationsFormProps) => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const formData = { ...initialData, ...values };
      await onSubmit(formData);
      hideModal();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({ ...initialData });
  }, []);

  const onGetClientAndSecret = async () => {
    try {
      setButtonLoading(true);
      const response = await getClientIdAndSecret();
      form.setFields([
        { name: "clientId", value: response.clientId },
        { name: "secret", value: response.clientSecret },
      ]);
    } catch (error) {
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  const appType = Form.useWatch("appSecurityType", form);

  return (
    <Form onFinish={handleFinish} initialValues={initialData} layout="vertical" form={form}>
      <Row gutter={20}>
        <Col span={21}>
          <Form.Item name="appSecurityType" label={t("Access type")}>
            <Radio.Group>
              <Radio value="JWT">{t("JWT Token - access of an application in the background (technical user)")}</Radio>
              <Radio value="OAUTH2">{t("OAuth2 Server - access to a user from another application")}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name="active" label={t("Active")} initialValue={true} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Client ID" required>
        <Row gutter={20}>
          <Col span={17}>
            <Form.Item name="clientId" noStyle rules={[{ required: true, message: t("This field is required") }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Button style={{ width: "100%" }} type="primary" onClick={onGetClientAndSecret} loading={buttonLoading}>
              {t("Generate ID and Secret")}
            </Button>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item name="name" label="Name" rules={[{ required: true, message: t("This field is required") }]}>
        <Input />
      </Form.Item>
      <Form.Item name="description" label={t("Description")}>
        <Input />
      </Form.Item>
      <Form.Item
        name="secret"
        label={t("Client secret (if empty, the previous secret remains current)")}
        rules={[{ required: isNew, message: t("This field is required") }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="redirectUrl" label={t("Redirect URL (form 3rd party apps, that use our OAuth2 server)")}>
        <Input />
      </Form.Item>
      <Form.Item
        name="businessUserId"
        label={t("Business user ID (only for JWT)")}
        rules={[
          {
            required: appType === "JWT",
            validator: (_, v) => {
              // If the app type is JWT, then the business user ID is required
              if (appType === "JWT" && !v && !isNaN(v)) {
                return Promise.reject(t("This field is required"));
              }
              if (Number.isNaN(v)) {
                return Promise.reject(t("Must be a integer", { ns: I18N_NS.VALIDATION }));
              }
              return Promise.resolve();
            },
            transform: (v) => (v && typeof v === "string" ? parseInt(v) : v),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Row gutter={20}>
        <Col span={12}>
          <Form.Item label={"Login URL (Authentication URL)"}>
            <ClipboardInput value={initialData.loginUrl} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={"Token URL"}>
            <ClipboardInput value={initialData.tokenURl} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="tokenLifeTimeInHours" label={t("Token life time in hours")}>
        <InputNumber />
      </Form.Item>

      <ButtonsRow justify="space-between">
        <Button type="default" onClick={hideModal}>
          {t("Cancel")}
        </Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {t("Submit")}
        </Button>
      </ButtonsRow>
    </Form>
  );
};
